import React, { useContext, useMemo, useRef } from 'react';
import { shallowEqual, useSelector } from 'umi';
import { isEqual } from 'lodash-es';
import { formatFilterValues, IsSingleDay } from '../../utils/utils';
import type { IServiceSelectorFormatProps } from './types';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { ALL_VALUE } from './constants';
import {
  defaultExtractor,
  extractByCascadeConfig,
  filterValuesByType,
  getNestedValue,
  setNestedValue,
  checkTriggerValue,
  processCustomFilters,
  getCustomFilterValue
} from './utils';
import { PERIODTIMEOBJ } from '../../_config/constant';

// 导出常量和工具函数，保持向后兼容
export { ALL_VALUE, PRESET_FIELD_MAPPINGS } from './constants';
export { createFieldMapping } from './utils';
export type { IFieldMappingConfig, IServiceSelectorFormatProps } from './types';

/**
 * ServiceSelectorFormat 公共组件
 * @description 根据配置规则，当 filter 中某个字段的值为特定值时，
 * 自动从 selector 中获取对应的数据来替换
 *
 * @example
 * // 基础用法：当 ua_network 的值为 255 时，从 selector 中获取 type 为 'UA' 的数据
 * <ServiceSelectorFormat
 *   data={{ filter: {...} }}
 *   fieldMappings={[
 *     {
 *       filterPath: 'custom_filters.ua_network',
 *       selectorPath: 'ua_network',
 *       cascadeConfig: {
 *         typeField: 'type',
 *         typeValue: 'UA',
 *         codeField: 'code'
 *       }
 *     }
 *   ]}
 * >
 *   <ChildComponent />
 * </ServiceSelectorFormat>
 *
 * @example
 * // 普通字段用法
 * <ServiceSelectorFormat
 *   data={{ filter: {...} }}
 *   fieldMappings={[
 *     {
 *       filterPath: 'zone_list',
 *       selectorPath: 'zone_list',
 *       extractor: (data) => data.map(item => item.zone_code)
 *     }
 *   ]}
 * >
 *   <ChildComponent />
 * </ServiceSelectorFormat>
 */
export default function ServiceSelectorFormat(props: IServiceSelectorFormatProps) {
  const {
    style,
    className,
    data: propsData,
    children,
    fieldMappings = [],
    ...restProps
  } = props || {};

  const { filterVal: contextFilterVal } = useContext(FilterContext);
  const filter = propsData?.filter
    || (Object.keys(contextFilterVal ?? {}).length > 0 ? contextFilterVal : undefined);

  // 使用 ref 缓存上一次的 filtersVal，避免不必要的重新计算
  const prevFilterRef = useRef<any>(null);
  const prevFiltersValRef = useRef<any>(null);

  // 使用 useMemo 结合深度比较来缓存 filtersVal
  const filtersVal = useMemo(() => {
    const newFiltersVal = formatFilterValues(filter);
    // 如果 filter 值相同，返回缓存的结果
    if (isEqual(prevFilterRef.current, filter) && prevFiltersValRef.current) {
      return prevFiltersValRef.current;
    }
    prevFilterRef.current = filter;
    prevFiltersValRef.current = newFiltersVal;
    return newFiltersVal;
  }, [filter]);

  const { selector } = useSelector((state: any) => ({
    selector: state?.dashboardSetting?.selector ?? {},
  }), shallowEqual);

  /**
   * 根据配置处理后的 filter 数据
   */
  const processedFilter = useMemo(() => {
    if (!filtersVal || fieldMappings.length === 0) {
      return filtersVal;
    }

    let result = { ...filtersVal };

    fieldMappings.forEach((mapping) => {
      const {
        filterPath,
        selectorPath,
        triggerValue = ALL_VALUE,
        extractor,
        cascadeConfig
      } = mapping;

      // 判断是否是 custom_filters 中的字段
      const isCustomFilter = filterPath.startsWith('custom_filters.');
      const actualFilterKey = isCustomFilter
        ? filterPath.replace('custom_filters.', '')
        : filterPath;

      // 获取 filter 中的当前值
      let currentValue: any;
      if (isCustomFilter) {
        currentValue = getCustomFilterValue(result.custom_filters, actualFilterKey);
      } else {
        currentValue = getNestedValue(result, filterPath);
      }

      // 从 selector 中获取数据
      const selectorData = getNestedValue(selector, selectorPath);
      if (!selectorData) {
        return;
      }

      // 检查是否满足替换条件（值为 255 等触发值）
      const shouldReplace = checkTriggerValue(currentValue, triggerValue);

      // 提取或过滤需要的数据
      let newValue: string[];

      if (shouldReplace) {
        // 情况1: 值为 255（全部），需要从 selector 中获取所有数据
        if (extractor) {
          // 使用自定义提取器
          newValue = extractor(selectorData, currentValue);
        } else if (cascadeConfig) {
          // 使用级联配置提取
          newValue = extractByCascadeConfig(selectorData, cascadeConfig);
        } else {
          // 使用默认提取器
          newValue = defaultExtractor(selectorData);
        }
      } else if (cascadeConfig?.filterByType && Array.isArray(currentValue)) {
        // 情况2: 值不是 255，但配置了 filterByType，需要过滤掉不属于该类型的值
        newValue = filterValuesByType(currentValue, selectorData, cascadeConfig);

        // 如果过滤后没有变化，不需要更新
        if (newValue.length === currentValue.length &&
            newValue.every((v, i) => v === currentValue[i])) {
          return;
        }
      } else {
        // 情况3: 不满足任何处理条件，跳过
        return;
      }

      // 更新结果
      if (isCustomFilter) {
        result = {
          ...result,
          custom_filters: processCustomFilters(
            result.custom_filters,
            actualFilterKey,
            newValue
          )
        };
      } else {
        result = setNestedValue(result, filterPath, newValue);
      }
    });

    return result;
  }, [filtersVal, selector, fieldMappings]);

  // 如果是单日单周单月 都视为没点击过筛选器（用于判断指标卡片数值是否取最后一天数据）
  const period = PERIODTIMEOBJ?.[filter?.timeType];
  const single = IsSingleDay({ rangeTime: filter?.rangeTime, period });

  /**
   * 传递给子组件的数据
   * 移除 filtersVal 依赖，因为 processedFilter 已经包含了 filtersVal 的处理结果
   */
  const data = useMemo(() => ({
    ...propsData,
    formattedFilter: processedFilter,
    is_confirm: single ? false : !filter?.diff
  }), [propsData, processedFilter]);

  return (
    <div className={className} style={style}>
      {React.Children.map(children, (child: any) => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child, {
          data,
          ...restProps
        } as any);
      })}
    </div>
  );
}
